/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import {createApp, defineAsyncComponent} from "vue";
import moment from "moment-timezone";
import * as MomentRange from "moment-range";
import vSelect from 'vue-select';
import store from "store"
import Inputmask from "inputmask";
import axios from "axios";
import swal from "sweetalert"
import WorkersIcon from "@operis/Icons/WorkersIcon.vue";

/**
 * Globally set locale to en-au for Luxon
 */
import {Settings} from 'luxon';
import {createPinia} from "pinia";
import {Loader} from "@googlemaps/js-api-loader";

Settings.defaultLocale = 'en-au';

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */

// Prepare global variables for use in vanilla JS
window.axios = axios;
window.store = store;
window.moment = MomentRange.extendMoment(moment);
window.swal = swal;
window.googleMapsApiLoader = new Loader({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    version: "weekly",
    libraries: ["places"]
});

const app = createApp({});

// code split these pages so that they are only sent to the browser when required
const DexAddNewCase = defineAsyncComponent(() => import("@components/Admin/Dex/Cases/AddNewCaseButton.vue"));
const ClientAppAccess = defineAsyncComponent(() => import("@components/Clients/ClientAppAccess/ClientAppAccess.vue"));
const TheEquipmentPage = defineAsyncComponent(() => import("@components/Data/Equipment/TheEquipmentPage.vue"));
const EquipmentUsageProfileBox = defineAsyncComponent(() => import("@components/Clients/Profile/EquipmentUsages/EquipmentUsageProfileBox.vue"));
const CarePlanProfileBox = defineAsyncComponent(() => import("@components/Clients/Profile/CarePlan/CarePlanProfileBox.vue"));
const ClientLinksProfileBox = defineAsyncComponent(() => import("@components/Clients/Profile/ClientLinks/ClientLinksProfileBox.vue"));
const ClientDocumentProfileBox = defineAsyncComponent(() => import("@components/Clients/Profile/ClientDocuments/ClientDocumentProfileBox.vue"));
const ClientInterestsProfileBox = defineAsyncComponent(() => import("@components/Clients/Profile/Interests/ClientInterestsProfileBox.vue"));
const SetPasswordPage = defineAsyncComponent(() => import("@components/Clients/signup/SetPasswordPage.vue"));
const LoginPage = defineAsyncComponent(() => import("@components/Authentication/Login/LoginPage.vue"));
const TwoFactorSetupPage = defineAsyncComponent(() => import("@components/Authentication/TwoFactorSetupPage.vue"));
const ResetPasswordPage = defineAsyncComponent(() => import("@components/Authentication/ResetPasswordPage.vue"));
const TheServicesTab = defineAsyncComponent(() => import("@components/Admin/ServiceOrganisations/Services/TheServicesTab.vue"));
const TheDocumentsPage = defineAsyncComponent(() => import("@components/Documents/TheDocumentsPage.vue"));
const EditDocument = defineAsyncComponent(() => import("@components/Documents/EditDocument.vue"));
const WorkerAppChats = defineAsyncComponent(() => import("@components/WorkerApp/Chats/WorkerAppChats.vue"));
const WorkerAppChatsModal = defineAsyncComponent(() => import("@components/WorkerApp/Chats/WorkerAppChatsModal.vue"));
const DocumentHistory = defineAsyncComponent(() => import("@components/Documents/DocumentHistory.vue"));
const WorkerAppSettings = defineAsyncComponent(() => import("@components/Settings/WorkerAppSettings.vue"));
const ClientAppSettings = defineAsyncComponent(() => import("@components/Settings/ClientAppSettings.vue"));
const TheTransNotesCategoriesPage = defineAsyncComponent(() => import("@components/Data/TransNotesCategories/TheTransNotesCategoriesPage.vue"))
const TheMdsLocationsPage = defineAsyncComponent(() => import("@components/Data/MdsLocations/TheMdsLocationsPage.vue"))
const ThePublicHolidaysPage = defineAsyncComponent(() => import("@components/Data/PublicHolidays/ThePublicHolidaysPage.vue"))
const TheLocalGovernmentAreasPage = defineAsyncComponent(() => import("@components/Data/LocalGovernmentAreas/TheLocalGovernmentAreasPage.vue"))
const ClientDocumentMiniList = defineAsyncComponent(() => import("@components/Clients/Documents/ClientDocumentMiniList.vue"));
const TheCustomConsentsPage = defineAsyncComponent(() => import("@components/Data/CustomConsents/TheCustomConsentsPage.vue"));
const TheCustomFieldsPage = defineAsyncComponent(() => import("@components/Data/CustomFields/TheCustomFieldsPage.vue"));
const TheWorkerCategoriesPage = defineAsyncComponent(() => import("@components/Data/WorkerCategories/TheWorkerCategoriesPage.vue"));
const TheCustomReportsPage = defineAsyncComponent(() => import("@components/Data/CustomReports/TheCustomReportsPage.vue"));
const IntegrationsDropboxIndexPage = defineAsyncComponent(() => import("@components/Integrations/Dropbox/IntegrationsDropboxIndexPage.vue"));
const AdminRunSheetSettingsPage = defineAsyncComponent(() => import("@components/Admin/RunSheetSettings/AdminRunSheetSettingsPage.vue"));
const DocumentImportIndexPage = defineAsyncComponent(() => import("@components/DocumentImport/DocumentImportIndexPage.vue"));
const DocumentImportPreviewPage = defineAsyncComponent(() => import("@components/DocumentImport/DocumentImportPreviewPage.vue"));
const TheHcpStatementsPage = defineAsyncComponent(() => import("@components/Models/HcpFundingPackageStatements/TheHcpStatementsPage.vue"));
const MessageCentreIndexPage = defineAsyncComponent(() => import("@components/MessageCentre/MessageCentreIndexPage.vue"));
const TheHcpFundingPackageAdjustmentReasonsPage = defineAsyncComponent(() => import("@components/Data/HcpFundingPackageAdjustmentReasons/TheHcpFundingPackageAdjustmentReasonsPage.vue"));
const AdminConnectedAppIndexPage = defineAsyncComponent(() => import("@components/Admin/ConnectedApp/AdminConnectedAppIndexPage.vue"));
const AdminConnectedAppAccessTokenIndexPage = defineAsyncComponent(() => import("@components/Admin/ConnectedApp/AccessToken/AdminConnectedAppAccessTokenIndexPage.vue"));
const TheNdisCataloguesPage = defineAsyncComponent(() => import("@components/Admin/NdisCatalogues/TheNdisCataloguesPage.vue"));
const TheServiceImportsPage = defineAsyncComponent(() => import("@components/Admin/ServiceImports/TheServiceImportsPage.vue"));
const TheAccountReportsPage = defineAsyncComponent(() => import("@components/Reports/AccountReports/TheAccountReportsPage.vue"));
const CustomFormTemplatesList = defineAsyncComponent(() => import("@components/CustomForms/CustomFormTemplatesList/CustomFormTemplatesList.vue"));
const CreateOrEditTemplate = defineAsyncComponent(() => import("@components/CustomForms/CustomFormTemplatesList/CreateOrEditTemplate.vue"));

app
    .component('VueSelect', vSelect)
    .component("DexAddNewCase", DexAddNewCase)
    .component("ClientAppAccess", ClientAppAccess)
    .component("SetPasswordPage", SetPasswordPage)
    .component("EquipmentUsageProfileBox", EquipmentUsageProfileBox)
    .component("ClientInterestsProfileBox", ClientInterestsProfileBox)
    .component("CarePlanProfileBox", CarePlanProfileBox)
    .component("ClientLinksProfileBox", ClientLinksProfileBox)
    .component("ClientDocumentProfileBox", ClientDocumentProfileBox)
    .component("LoginPage", LoginPage)
    .component("TwoFactorSetupPage", TwoFactorSetupPage)
    .component("ResetPasswordPage", ResetPasswordPage)
    .component("TheServicesTab", TheServicesTab)
    .component("TheDocumentsPage", TheDocumentsPage)
    .component("TheEquipmentPage", TheEquipmentPage)
    .component("EditDocument", EditDocument)
    .component("WorkerAppChats", WorkerAppChats)
    .component("WorkerAppChatsModal", WorkerAppChatsModal)
    .component("DocumentHistory", DocumentHistory)
    .component("WorkerAppSettings", WorkerAppSettings)
    .component("ClientAppSettings", ClientAppSettings)
    .component("TheTransNotesCategoriesPage", TheTransNotesCategoriesPage)
    .component("TheDepartmentsPage", defineAsyncComponent(() => import("@components/Data/Departments/TheDepartmentsPage.vue")))
    .component("EditDepartmentPage", defineAsyncComponent(() => import("@components/Data/Departments/EditDepartmentPage.vue")))
    .component("TheMdsLocationsPage", TheMdsLocationsPage)
    .component("ThePublicHolidaysPage", ThePublicHolidaysPage)
    .component("TheLocalGovernmentAreasPage", TheLocalGovernmentAreasPage)
    .component("ClientDocumentMiniList", ClientDocumentMiniList)
    .component("TheCustomConsentsPage", TheCustomConsentsPage)
    .component("TheCustomFieldsPage", TheCustomFieldsPage)
    .component("TheWorkerCategoriesPage", TheWorkerCategoriesPage)
    .component("TheCustomReportsPage", TheCustomReportsPage)
    .component("IntegrationsDropboxIndexPage", IntegrationsDropboxIndexPage)
    .component("AdminRunSheetSettingsPage", AdminRunSheetSettingsPage)
    .component("DocumentImportIndexPage", DocumentImportIndexPage)
    .component("DocumentImportPreviewPage", DocumentImportPreviewPage)
    .component("MessageCentreIndexPage", MessageCentreIndexPage)
    .component("TheHcpFundingPackageStatementsPage", TheHcpStatementsPage)
    .component("TheHcpFundingPackageAdjustmentReasonsPage", TheHcpFundingPackageAdjustmentReasonsPage)
    .component("TheAccountReportsPage", TheAccountReportsPage)
    .component("AdminConnectedAppIndexPage", AdminConnectedAppIndexPage)
    .component("AdminConnectedAppAccessTokenIndexPage", AdminConnectedAppAccessTokenIndexPage)
    .component("TheNdisCataloguesPage", TheNdisCataloguesPage)
    .component("TheServiceEditPage", defineAsyncComponent(() => import("@pages/Services/TheServiceEditPage.vue")))
    .component("TheServiceImportsPage", TheServiceImportsPage)
    .component("TheCreateServiceImportLogPage", defineAsyncComponent(() => import("@components/Admin/ServiceImports/TheCreateServiceImportLogPage.vue")))
    .component("NdisIntegrationHomepage", defineAsyncComponent(() => import("@components/NdisIntegration/NdisIntegrationHomepage.vue")))
    .component("TheCurrentPortalUsersPage", defineAsyncComponent(() => import("@components/Data/CurrentUsers/PortalUsers/TheCurrentPortalUsersPage.vue")))
    .component("SmsReplyNumberVerifyButton", defineAsyncComponent(() => import("@components/Settings/Components/SmsReplyNumberVerifyButton.vue")))
    .component("SmsReplyNumberReVerifyButton", defineAsyncComponent(() => import("@components/Settings/Components/SmsReplyNumberReVerifyButton.vue")))
    .component("SmsReplyNumberDeleteButton", defineAsyncComponent(() => import("@components/Settings/Components/SmsReplyNumberDeleteButton.vue")))
    .component("XeroItemsUpdateSalesAccountsButton", defineAsyncComponent(() => import("@components/Xero/XeroItemsUpdateSalesAccountsButton.vue")))
    .component('custom-form-templates-list', CustomFormTemplatesList)
    .component('create-or-edit-template', CreateOrEditTemplate)

    // Roster Optimiser pages
    .component("TheRosterOptimiserPage", defineAsyncComponent(() => import("@operis/Pages/TheRosterOptimiserPage.vue")))
    .component("TheRosterDashboardPage", defineAsyncComponent(() => import("@operis/Pages/TheRosterDashboardPage.vue")))
    .component("TheTransactionOptimiseTab", defineAsyncComponent(() => import("@operis/Pages/TheTransactionOptimiseTab.vue")))
    .component("WorkersIcon", WorkersIcon)
    .component("TheOperisApiRequestsPage", defineAsyncComponent(() => import("@operis/Pages/TheOperisApiRequestsPage.vue")))
    .component("TheOperisChooseLocationPage", defineAsyncComponent(() => import("@operis/Pages/TheOperisChooseLocationPage.vue")))
    .component("TheRosterDashboardChooseLocationPage", defineAsyncComponent(() => import("@operis/Pages/TheRosterDashboardChooseLocationPage.vue")))
    .component("CustomFormSubmissionExportButton", defineAsyncComponent(() => import("@components/CustomForms/Submission/CustomFormSubmissionExportButton.vue")))
    
    // Support at home pages
    .component("TheClientSupportAtHomePage", defineAsyncComponent(() => import("@sah/Pages/TheClientSupportAtHomePage.vue")))
    .component("TheSupportAtHomeClientClientBudgetIndexPage", defineAsyncComponent(() => import("@sah/Pages/Client/ClientBudget/Index.vue")))
    .component("TheSupportAtHomeClientBudgetIndexPage", defineAsyncComponent(() => import("@sah/Pages/ClientBudget/Index.vue")))
    .component("TheSupportAtHomeClientBudgetShowPage", defineAsyncComponent(() => import("@sah/Pages/ClientBudget/Show.vue")))
    .component("TheSupportAtHomeBudgetUsageShowPage", defineAsyncComponent(() => import("@sah/Pages/BudgetUsage/Show.vue")))
    .component("TheSupportAtHomeServiceProviderBudgetShowPage", defineAsyncComponent(() => import("@sah/Pages/ServiceProviderBudget/Show.vue")))
    .component("TheSyncClientsPage", defineAsyncComponent(() => import("@sah/Pages/TheSyncClientsPage.vue")))
    .component("TheSupportAtHomeClientIndexPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeClientIndexPage.vue")))
    .component("TheSupportAtHomeServicesPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeServicesPage.vue")))
    .component("TheSupportAtHomeApiRequestsPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeApiRequestsPage.vue")))
    .component("TheSupportAtHomeClaimingBatchesPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeClaimingBatchesPage.vue")))
    .component("TheSupportAtHomeClaimingBatchPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeClaimingBatchPage.vue")))
    .component("TheSupportAtHomeClaimingBulkUploadsPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeClaimingBulkUploadsPage.vue")))
    .component("TheSupportAtHomeClaimingBulkUploadShowPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeClaimingBulkUploadShowPage.vue")))
    .component("TheSupportAtHomeGeneratedBulkExportsPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeGeneratedBulkExportsPage.vue")))
    .component("TheSupportAtHomeStatementsLandingPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeStatementsLandingPage.vue")))
    .component("TheSupportAtHomeActionItemsPage", defineAsyncComponent(() => import("@sah/Pages/TheSupportAtHomeActionItemsPage.vue")))
    
    // service agreements pages
    .component("TheServiceAgreementsPage", defineAsyncComponent(() => import("@service-agreements/Pages/ServiceAgreementsPage.vue")))
    .component("TheClientAcceptanceFormPage", defineAsyncComponent(() => import("@service-agreements/Pages/ClientAcceptanceFormPage.vue")))
    .component("ClientServiceAgreementProfileBox", defineAsyncComponent(() => import("@service-agreements/Components/ClientProfileBox.vue")))
    
    .component("TheRosterOptimiserReadinessPage", defineAsyncComponent(() => import("@components/Reports/RosterOptimiserReadiness/TheRosterOptimiserReadinessPage.vue")))
    .component("DashBoardPage", defineAsyncComponent(() => import("@components/DashBoard/page.vue")))
    // Allow a checkbox to be set to an "indeterminate" [-] state
    .directive("indeterminate", (el, binding) => el.indeterminate = Boolean(binding.value))
    .directive("mask", (el, binding) => Inputmask(binding.value).mask(el))
    .use(createPinia());

app.mount("#app");

// call some app-level initialisation functions
import { useWorkerMetadataStore } from "@assets/js/stores/WorkerMetadataStore";
const workerMetadataStore = useWorkerMetadataStore();
workerMetadataStore.fetchMetadata();

// datastores - NOT pinia stores
import ServicesStore from '@legacy-src/Datastores/Services';
import BusinessFunctionsStore from '@sah/Legacy/Datastores/BusinessFunctions';
import BusinessFunctionItemsStore from '@sah/Legacy/Datastores/BusinessFunctionItems';
import HealthProfessionalTypesStore from '@sah/Legacy/Datastores/HealthProfessionalTypes';
import ServiceMetadataStore from '@sah/Legacy/Datastores/ServiceMetadata';
import ServiceListItemsStore from '@sah/Legacy/Datastores/ServiceListItems';
import WraparoundsStore from '@sah/Legacy/Datastores/Wraparounds';
import ClientBudgetsStore from '@sah/Legacy/Datastores/ClientBudgets';
import ClientMetadataStore from '@sah/Legacy/Datastores/ClientMetadata';
import HomeSupportItemsStore from '@sah/Legacy/Datastores/HomeSupportItems';

// Expose legacy datastores globally so we can use them in legacy javascript.
// NOTE! if you try to import datastores into legacy js it will break everything because
// vite attempts to break the common dependency into its own file, and transaction.js is not
// being treated as a module in the browser, so it can't import chunks. You need to add these
// datastores here in order to use them in legacy js.
window.LegacyDatastores = {
    services: ServicesStore,
    business_functions: BusinessFunctionsStore,
    business_function_items: BusinessFunctionItemsStore,
    health_professional_types: HealthProfessionalTypesStore,
    service_list_items: ServiceListItemsStore,
    service_metadata: ServiceMetadataStore,
    wraparounds: WraparoundsStore,
    client_budgets: ClientBudgetsStore,
    client_metadata: ClientMetadataStore,
    home_support_items: HomeSupportItemsStore
};